<template>
  <div class="inspection-step-body">
    <div class="inspection-step-body__header">
      <div class="inspection-step-body__type-and-step">
        <span class="inspection-step-body__type-chip">
          <span class="inspection-step-body__type-icon" aria-hidden="true">
            <CompetenceCategoryIcon :icon="category" />
          </span>
          <span class="inspection-step-body__type-label">{{ categoryLabel }}</span>
        </span>
        <span v-if="indexLabel" class="inspection-step-body__index">{{ indexLabel }}</span>
      </div>

      <component
        :is="isPlayLink ? 'button' : 'span'"
        v-if="hasPlayChip"
        v-bind="playChipAttrs"
        class="inspection-step-body__play-chip"
        :class="{ 'inspection-step-body__play-chip--link': isPlayLink }"
        v-on="isPlayLink ? { click: onPlayClick } : {}"
      >
        <span class="inspection-step-body__play-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.33" />
            <path
              d="M6.5 5.6L10.4 8L6.5 10.4V5.6Z"
              stroke="currentColor"
              stroke-width="1.33"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <span>{{ playLabel }}</span>
      </component>
    </div>

    <p v-if="inspectorAction" class="inspection-step-body__action">{{ inspectorAction }}</p>

    <div v-if="lookFor" class="inspection-step-body__look-for">
      <span class="inspection-step-body__look-for-label">Look for</span>
      <p class="inspection-step-body__text">{{ lookFor }}</p>
    </div>

    <div v-if="passCriteria || failCriteria" class="inspection-step-body__criteria">
      <div
        v-if="passCriteria"
        class="inspection-step-body__criteria-block inspection-step-body__criteria-block--pass"
      >
        <span class="inspection-step-body__criteria-label inspection-step-body__criteria-label--pass">
          Pass criteria
        </span>
        <p class="inspection-step-body__text">{{ passCriteria }}</p>
      </div>
      <div
        v-if="failCriteria"
        class="inspection-step-body__criteria-block inspection-step-body__criteria-block--fail"
      >
        <span class="inspection-step-body__criteria-label inspection-step-body__criteria-label--fail">
          Fail criteria
        </span>
        <p class="inspection-step-body__text">{{ failCriteria }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import CompetenceCategoryIcon from "@components/CompetenceCategoryIcon/CompetenceCategoryIcon.vue";

const props = defineProps({
  category: {
    type: String,
    default: "knowledge",
    validator: (value) => ["knowledge", "ability", "behaviour"].includes(value),
  },
  /** What the inspector should observe (the step's main text). */
  inspectorAction: {
    type: String,
    default: "",
  },
  lookFor: {
    type: String,
    default: "",
  },
  passCriteria: {
    type: String,
    default: "",
  },
  failCriteria: {
    type: String,
    default: "",
  },
  /** Video range such as "00:00-01:00". The Play chip renders only when this is set. */
  timestampRef: {
    type: String,
    default: "",
  },
  /** When set, the Play chip is a button that emits `timestamp-click` with this URL. */
  videoJumpUrl: {
    type: String,
    default: "",
  },
  stepIndex: {
    type: Number,
    default: null,
  },
  stepCount: {
    type: Number,
    default: null,
  },
  /** Set to false where the host card already shows the step number (the editor). */
  showIndex: {
    type: Boolean,
    default: true,
  },
  showPlay: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["timestamp-click"]);

const categoryLabel = computed(
  () => props.category.charAt(0).toUpperCase() + props.category.slice(1)
);

const indexLabel = computed(() => {
  if (!props.showIndex || props.stepIndex == null) return "";
  return props.stepCount != null
    ? `Step ${props.stepIndex} of ${props.stepCount}`
    : `Step ${props.stepIndex}`;
});

const hasPlayChip = computed(() => props.showPlay && Boolean(props.timestampRef));
const isPlayLink = computed(() => hasPlayChip.value && Boolean(props.videoJumpUrl));
const playChipAttrs = computed(() => (isPlayLink.value ? { type: "button" } : {}));

/** "00:00-01:00" -> "00:00 - 1:00" (leading zero dropped on the end time). */
const playLabel = computed(() => {
  const parts = props.timestampRef.split("-");
  if (parts.length !== 2) return `Play ${props.timestampRef}`;
  const end = parts[1].trim().replace(/^0(\d:\d{2})$/, "$1");
  return `Play ${parts[0].trim()} - ${end}`;
});

function onPlayClick() {
  emit("timestamp-click", props.videoJumpUrl);
}
</script>

<style scoped>
.inspection-step-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px 16px 20px;
  font-family: Inter, sans-serif;
}

.inspection-step-body__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.inspection-step-body__type-and-step {
  display: flex;
  align-items: center;
  gap: 10px;
}

.inspection-step-body__type-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px 2px 8px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #f9fafb;
}

.inspection-step-body__type-icon {
  display: inline-flex;
  width: 16px;
  height: 16px;
}

.inspection-step-body__type-icon :deep(svg) {
  width: 16px;
  height: 16px;
}

.inspection-step-body__type-label,
.inspection-step-body__index {
  color: #475467;
  font-size: 12px;
  font-weight: 500;
  line-height: 14.52px;
}

.inspection-step-body__play-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px 4px 8px;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  background: #ffffff;
  color: #2c0cfa;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.inspection-step-body__play-chip--link {
  cursor: pointer;
}

.inspection-step-body__play-chip--link:hover {
  background: #f9fafb;
}

.inspection-step-body__play-chip--link:focus-visible {
  outline: 2px solid #2c0cfa;
  outline-offset: 2px;
}

.inspection-step-body__play-icon {
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
}

.inspection-step-body__action,
.inspection-step-body__text {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.inspection-step-body__action {
  color: #101828;
}

.inspection-step-body__text {
  color: #475467;
}

.inspection-step-body__look-for {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.inspection-step-body__look-for-label {
  color: #344054;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.inspection-step-body__criteria {
  display: flex;
  gap: 12px;
}

.inspection-step-body__criteria-block {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 12px;
  border: 1px solid;
  border-radius: 8px;
}

.inspection-step-body__criteria-block--pass {
  border-color: #abefc6;
  background: #ecfdf3;
}

.inspection-step-body__criteria-block--fail {
  border-color: #fecdca;
  background: #fef3f2;
}

.inspection-step-body__criteria-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.inspection-step-body__criteria-label--pass {
  color: #067647;
}

.inspection-step-body__criteria-label--fail {
  color: #b42318;
}
</style>
